import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Divider,
  TextField,
  Switch,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { postShift, updateShift } from "../../services/shifts.service";

const AddShift = (props) => {
  const { setOpenDrawer, formType, selectedData, GetData } = props;
  const disabled = formType === "view";
  const formRef = useRef(null);

  const [initialValues, setInitialValues] = useState({
    shiftName: "",
    expectedInTime: null,
    expectedOutTime: null,
    is10HourShift: false,
  });

  const validationSchema = Yup.object().shape({
    shiftName: Yup.string().required("Shift Name is required"),
    expectedInTime: Yup.mixed().required("Expected In Time is required"),
    expectedOutTime: Yup.mixed()
      .required("Expected Out Time is required")
      .test(
        "duration",
        "Shift duration must match the selected shift type",
        function (value) {
          const { parent } = this;
          if (!parent) {
            return true;
          }
          const { expectedInTime, is10HourShift } = parent;
          if (!expectedInTime || !value) {
            return true;
          }
          const inTime = dayjs(expectedInTime);
          const outTime = dayjs(value);
          const diffMinutes = outTime.diff(inTime, "minute");
          const diffHours = diffMinutes / 60;

          const isValid = is10HourShift
            ? Math.abs(diffHours - 10) < 0.1
            : Math.abs(diffHours - 8) < 0.1;

          if (!isValid) {
            return this.createError({
              message: is10HourShift
                ? "Shift must be 10 hours long"
                : "Shift must be 8 hours long",
            });
          }
          return true;
        }
      ),
  });

  useEffect(() => {
    if (formType === "edit" || formType === "view") {
      const inTime = dayjs(selectedData.expectedInTime, "hh:mm A");
      const outTime = dayjs(selectedData.expectedOutTime, "hh:mm A");
      const diff = outTime.diff(inTime, "hour");
      setInitialValues({
        shiftName: selectedData.shiftName || "",
        expectedInTime: inTime,
        expectedOutTime: outTime,
        is10HourShift: diff === 10,
      });
    }
  }, [formType, selectedData]);

  const onSubmit = async (values) => {
    try {
      const apiMethod =
        formType === "edit"
          ? updateShift(selectedData._id, values)
          : postShift(values);
      const res = await apiMethod;
      if (formType === "edit") {
        ToastSuccess(res.message);
      }
      setOpenDrawer(false);
      GetData();
    } catch (error) {
      ToastError(error?.data?.message);
    }
  };

  return (
    <div className="addRenderingPopup">
      <Box className="addContainerHeader">
        <Typography className="headerTitle">
          {formType === "edit" ? "Edit" : formType === "view" ? "View" : "Add"}{" "}
          Shift
        </Typography>
        <CloseIcon
          sx={{ fontSize: "10px" }}
          className="Close-Icon"
          onClick={() => setOpenDrawer(false)}
        />
      </Box>
      <Divider sx={{ opacity: 1 }} />
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        innerRef={formRef}
      >
        {({ values, setFieldValue, touched, errors }) => (
          <Form>
            <Box className="addContainerCenter" mb={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} mt={1}>
                  <Typography className="inputLabel">
                    Shift Name <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Field
                    name="shiftName"
                    className="InputField"
                    placeholder="Enter Shift Name"
                    disabled={disabled}
                  />
                  <div className="form-error">
                    {touched.shiftName && errors.shiftName && errors.shiftName}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} mt={1}>
                  <Typography className="inputLabel">
                    Expected In Time <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={values.expectedInTime}
                      onChange={(newValue) =>
                        setFieldValue("expectedInTime", newValue)
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                      disabled={disabled}
                    />
                  </LocalizationProvider>
                  <div className="form-error">
                    {touched.expectedInTime &&
                      errors.expectedInTime &&
                      errors.expectedInTime}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} mt={1}>
                  <Typography className="inputLabel">
                    Expected Out Time <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TimePicker
                      value={values.expectedOutTime}
                      onChange={(newValue) =>
                        setFieldValue("expectedOutTime", newValue)
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                      disabled={disabled}
                    />
                  </LocalizationProvider>
                  <div className="form-error">
                    {touched.expectedOutTime &&
                      errors.expectedOutTime &&
                      errors.expectedOutTime}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} mt={1}>
                  <Typography className="inputLabel">10 Hour Shift</Typography>
                  <Field
                    name="is10HourShift"
                    component={({ field, form }) => (
                      <Switch
                        {...field}
                        checked={field.value}
                        onChange={(e) =>
                          form.setFieldValue(field.name, e.target.checked)
                        }
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
            <Divider sx={{ opacity: 1 }} />
            <Grid className="addboxFooter">
              <Button
                className="cancelButton"
                onClick={() => setOpenDrawer(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="saveButton"
                sx={{ display: disabled ? "none" : "flex" }}
              >
                Save
              </Button>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddShift;
